import { useMemo } from "react";
import { Flex } from "styles/utility-components";

import EditColumnsButton from "lib/core_components/EditColumnsButton";
import { Table } from "lib/core_components/Table";
import { SpendFilters } from "lib/hooks/api/spend/spendFilters";
import { EventDataTypes } from "lib/tracking";
import { SpendExportGroupBy } from "lib/types/graphQLEnums";
import { ColumnSetting, TableSettings } from "lib/types/models";
import { TransactionSort, useTransactionsTable } from "./hooks/useTransactionsTable";
import { TRANSACTION_COLUMNS, TransactionColumn } from "./columns";
import ExportDataButton from "./ExportDataButton";
import { EmptyState } from "./pageStates";

type Props = {
  filters: SpendFilters;
  columnFields: TransactionColumn[];
  onColumnFieldsChange: (fields: TransactionColumn[]) => void;
  sort: TransactionSort;
  onSortChange: (sort: TransactionSort) => void;
};

/**
 * The transactions behind the filters, nothing aggregated. Owns its query, its export and
 * its own empty state, so the page only has to decide that this is the view on screen.
 */
export function SpendTransactionsView({
  filters,
  columnFields,
  onColumnFieldsChange,
  sort,
  onSortChange,
}: Props) {
  const table = useTransactionsTable(filters, columnFields, { sort, onSortChange });

  // Memoised: the column picker re-syncs its list whenever this array's identity changes.
  const selectedColumns = useMemo(
    () =>
      columnFields
        .map((field) => TRANSACTION_COLUMNS.find((c) => c.field === field))
        .filter((c): c is ColumnSetting<TransactionColumn> => c !== undefined),
    [columnFields],
  );

  // A failed request also returns no rows and stops loading, which looks just like a search
  // that matched nothing. Only call it empty once we know the question was answered.
  if (!table.isLoading && !table.isError && table.pagination.total === 0) {
    return <EmptyState filters={filters} />;
  }

  return (
    <Flex column gap={16}>
      <Flex justifyContent="space-between" alignItems="center" gap={8}>
        <ExportDataButton
          filters={filters}
          totalRows={table.pagination.total ?? 0}
          isLoadingTotalRows={table.isLoading}
          groupBy={SpendExportGroupBy.None}
          sort={table.sort}
        />
        <EditColumnsButton<TableSettings<TransactionColumn>>
          allAvailableColumns={TRANSACTION_COLUMNS}
          selectedColumns={selectedColumns}
          onNewColumnSettingChange={(columns) => onColumnFieldsChange(columns.map((c) => c.field))}
          dataType={EventDataTypes.spend}
          trackLegacyReorderEvent={false}
        />
      </Flex>
      <Table
        ariaLabel="Spend table"
        columns={table.columns}
        dataSource={table.data}
        pagination={{ ...table.pagination, showSizeChanger: true }}
        rowKey={table.rowKey}
        loading={table.isLoading}
        onChange={(pagination, _filters, sorter) => {
          table.pagination.onChange(pagination.current || 1, pagination.pageSize);
          table.onSortChange(sorter);
        }}
        isError={table.isError}
      />
    </Flex>
  );
}
